import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { useCurrency } from '../contexts/CurrencyContext';
import Layout from '../components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Users, DollarSign, TrendingUp, Target, Trash2, Mail, CheckCircle, XCircle, Megaphone, FileText, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import Price from '../components/Price';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState(null);
  const [strategies, setStrategies] = useState([]);
  const [editingPlan, setEditingPlan] = useState(null);
  const [newPrice, setNewPrice] = useState('');
  const [newYearlyPrice, setNewYearlyPrice] = useState('');
  const [newDiscountPrice, setNewDiscountPrice] = useState('');
  const [newCurrency, setNewCurrency] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview'); // overview, users, plans, strategies
  
  // Announcement state
  const [announcementTitle, setAnnouncementTitle] = useState('');
  const [announcementMessage, setAnnouncementMessage] = useState('');
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [userCounts, setUserCounts] = useState({ all: 0, free: 0, premium: 0, tipster: 0 });
  const [sendingAnnouncement, setSendingAnnouncement] = useState(false);
  const [announcementHistory, setAnnouncementHistory] = useState([]);
  const [announcementPage, setAnnouncementPage] = useState(1);
  const [announcementPagination, setAnnouncementPagination] = useState({});
  
  // Confirmation dialogs state
  const [deleteUserDialog, setDeleteUserDialog] = useState({ open: false, userId: null });
  const [announcementDialog, setAnnouncementDialog] = useState({ open: false, count: 0 });
  
  // Notification statistics state
  const [notificationStats, setNotificationStats] = useState(null);
  const [loadingNotificationStats, setLoadingNotificationStats] = useState(false);
  
  // Email modal state
  const [emailModal, setEmailModal] = useState({ open: false, user: null });
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    fetchData();
    fetchAnnouncementHistory();
    fetchNotificationStatistics();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('session_token');
    try {
      const [statsRes, usersRes, plansRes, strategiesRes, countsRes] = await Promise.all([
        axios.get(`${API}/admin/statistics`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/users`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/subscription-plans`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/betting-strategies`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/users/count-by-role`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data);
      setPlans(plansRes.data.plans);
      setStrategies(strategiesRes.data);
      setUserCounts(countsRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const fetchNotificationStatistics = async () => {
    setLoadingNotificationStats(true);
    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.get(`${API}/admin/notification-statistics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotificationStats(response.data);
    } catch (error) {
      console.error('Error fetching notification statistics:', error);
      toast.error('Failed to load notification statistics');
    } finally {
      setLoadingNotificationStats(false);
    }
  };

  const fetchAnnouncementHistory = async (page = 1) => {
    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.get(`${API}/admin/announcements/history`, {
        params: { page, limit: 5 },
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnnouncementHistory(response.data.announcements);
      setAnnouncementPagination(response.data.pagination);
      setAnnouncementPage(page);
    } catch (error) {
      console.error('Error fetching announcement history:', error);
    }
  };

  const handleUpdateSubscription = async (userId, plan) => {
    const token = localStorage.getItem('session_token');
    try {
      await axios.put(
        `${API}/admin/users/${userId}/subscription?plan=${plan}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Subscription updated successfully');
      fetchData();
    } catch (error) {
      console.error('Error updating subscription:', error);
      toast.error('Failed to update subscription');
    }
  };

  const handleDeleteUser = async (userId) => {
    setDeleteUserDialog({ open: true, userId });
  };

  const confirmDeleteUser = async () => {
    const userId = deleteUserDialog.userId;
    setDeleteUserDialog({ open: false, userId: null });

    const token = localStorage.getItem('session_token');
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('User deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  const handleResendVerification = async (userId, userEmail) => {
    const token = localStorage.getItem('session_token');
    try {
      await axios.post(`${API}/admin/users/${userId}/resend-verification`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Verification email sent to ${userEmail}`);
    } catch (error) {
      console.error('Error resending verification:', error);
      toast.error(error.response?.data?.detail || 'Failed to resend verification email');
    }
  };

  const handleRoleToggle = (role) => {
    if (role === 'all') {
      if (selectedRoles.includes('all')) {
        setSelectedRoles([]);
      } else {
        setSelectedRoles(['all']);
      }
    } else {
      // Remove 'all' if specific role is toggled
      let newRoles = selectedRoles.filter(r => r !== 'all');
      
      if (newRoles.includes(role)) {
        newRoles = newRoles.filter(r => r !== role);
      } else {
        newRoles.push(role);
      }
      
      setSelectedRoles(newRoles);
    }
  };

  const getTargetUserCount = () => {
    if (selectedRoles.includes('all')) {
      return userCounts.all;
    }
    
    let count = 0;
    selectedRoles.forEach(role => {
      count += userCounts[role] || 0;
    });
    return count;
  };

  const handleSendAnnouncement = async () => {
    if (!announcementTitle.trim() || !announcementMessage.trim()) {
      toast.error('Title and message are required');
      return;
    }

    if (selectedRoles.length === 0) {
      toast.error('Please select at least one target group');
      return;
    }

    const targetCount = getTargetUserCount();
    setAnnouncementDialog({ open: true, count: targetCount });
  };

  const confirmSendAnnouncement = async () => {
    setAnnouncementDialog({ open: false, count: 0 });

    setSendingAnnouncement(true);
    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.post(`${API}/admin/announcements/send`, {
        title: announcementTitle,
        message: announcementMessage,
        target_roles: selectedRoles
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Announcement sent to ${response.data.users_notified} user(s)!`);
      
      // Clear form
      setAnnouncementTitle('');
      setAnnouncementMessage('');
      setSelectedRoles([]);
      
      // Refresh history
      fetchAnnouncementHistory();
    } catch (error) {
      console.error('Error sending announcement:', error);
      toast.error(error.response?.data?.detail || 'Failed to send announcement');
    } finally {
      setSendingAnnouncement(false);
    }
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    if (!window.confirm('Are you sure you want to delete this announcement from history?')) {
      return;
    }

    const token = localStorage.getItem('session_token');
    try {
      await axios.delete(`${API}/admin/announcements/history/${announcementId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Announcement deleted from history');
      fetchAnnouncementHistory();
    } catch (error) {
      console.error('Error deleting announcement:', error);
      toast.error('Failed to delete announcement');
    }
  };

  const handleEditPlan = (planId, planData) => {
    setEditingPlan(planId);
    setNewPrice(planData.price.toString());
    setNewYearlyPrice(planData.yearly_price ? planData.yearly_price.toString() : '');
    setNewDiscountPrice(planData.discount_price ? planData.discount_price.toString() : '');
  };

  const handleUpdatePrice = async (planId) => {
    const token = localStorage.getItem('session_token');
    try {
      const params = new URLSearchParams();
      if (newPrice) params.append('monthly_price', newPrice);
      if (newYearlyPrice) params.append('yearly_price', newYearlyPrice);
      if (newDiscountPrice) params.append('discount_price', newDiscountPrice);
      if (newCurrency) params.append('currency', newCurrency);
      
      await axios.put(`${API}/admin/subscription-plans/${planId}?${params.toString()}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Plan updated successfully');
      setEditingPlan(null);
      setNewPrice('');
      setNewYearlyPrice('');
      setNewDiscountPrice('');
      setNewCurrency('');
      fetchData();
    } catch (error) {
      console.error('Error updating price:', error);
      toast.error(error.response?.data?.detail || 'Failed to update plan');
    }
  };

  const handleTogglePlan = async (planId, currentlyEnabled) => {
    const token = localStorage.getItem('session_token');
    try {
      const params = new URLSearchParams();
      params.append('enabled', !currentlyEnabled);
      
      await axios.put(`${API}/admin/subscription-plans/${planId}?${params.toString()}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Plan ${!currentlyEnabled ? 'enabled' : 'disabled'} successfully`);
      fetchData();
    } catch (error) {
      console.error('Error toggling plan:', error);
      toast.error(error.response?.data?.detail || 'Failed to toggle plan');
    }
  };

  const handleSendEmail = async () => {
    if (!emailSubject.trim() || !emailMessage.trim()) {
      toast.error('Subject and message are required');
      return;
    }

    setSendingEmail(true);
    const token = localStorage.getItem('session_token');

    try {
      await axios.post(
        `${API}/admin/send-email`,
        {
          user_id: emailModal.user.id,
          subject: emailSubject,
          message: emailMessage
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Email sent to ${emailModal.user.email}`);
      setEmailModal({ open: false, user: null });
      setEmailSubject('');
      setEmailMessage('');
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
            <p className="text-white text-lg">Loading admin dashboard...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="admin-dashboard-page" className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col gap-4 mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-white">Admin Dashboard</h1>
            
            {/* Colored action buttons - First row */}
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => navigate('/admin/crm')}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Users className="w-4 h-4 mr-2" />
                User Management (CRM)
              </Button>
              <Button
                onClick={() => navigate('/admin/tips')}
                className="bg-green-600 hover:bg-green-700"
              >
                Manage Tips
              </Button>
              <Button
                onClick={() => navigate('/admin/withdrawals')}
                className="bg-yellow-600 hover:bg-yellow-700"
              >
                Manage Withdrawals
              </Button>
              <Button
                onClick={() => navigate('/admin/payments')}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Payment Management
              </Button>
              <Button
                onClick={() => navigate('/admin/strategies')}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Manage Strategies
              </Button>
              <Button
                onClick={() => navigate('/admin/analytics')}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Analytics Dashboard
              </Button>
            </div>
            
            {/* Outline settings buttons - Second row */}
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => navigate('/admin/tables')}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                Tables
              </Button>
              <Button
                onClick={() => navigate('/admin/ads')}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                Ads
              </Button>
              <Button
                onClick={() => navigate('/admin/smtp-settings')}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                SMTP Settings
              </Button>
              <Button
                onClick={() => navigate('/admin/payment-settings')}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                Payment Settings
              </Button>
              <Button
                onClick={() => navigate('/admin/general-settings')}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                General Settings
              </Button>
              <Button
                onClick={() => navigate('/admin/blog')}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                Blog Management
              </Button>
              <Button
                onClick={() => navigate('/admin/affiliate-banners')}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                Affiliate Tools
              </Button>
              <Button
                onClick={() => navigate('/admin/prediction-history')}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                AI Predictions
              </Button>
            </div>
          </div>

          {/* Admin Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="bg-gradient-to-br from-blue-500/20 to-blue-700/20 border-blue-500/30 stat-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Total Users</p>
                    <p data-testid="admin-stat-total-users" className="text-3xl font-bold text-white">{stats?.total_users || 0}</p>
                  </div>
                  <Users className="w-12 h-12 text-blue-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-500/20 to-green-700/20 border-green-500/30 stat-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Premium Users</p>
                    <p data-testid="admin-stat-premium-users" className="text-3xl font-bold text-white">{stats?.premium_users || 0}</p>
                  </div>
                  <TrendingUp className="w-12 h-12 text-green-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-500/20 to-purple-700/20 border-purple-500/30 stat-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Total Bets</p>
                    <p data-testid="admin-stat-total-bets" className="text-3xl font-bold text-white">{stats?.total_bets || 0}</p>
                  </div>
                  <Target className="w-12 h-12 text-purple-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-emerald-500/20 to-emerald-700/20 border-emerald-500/30 stat-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Total Revenue</p>
                    <p data-testid="admin-stat-total-revenue" className="text-3xl font-bold text-white">
                      <Price amount={stats?.total_revenue || 0} />
                    </p>
                  </div>
                  <DollarSign className="w-12 h-12 text-emerald-400" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Announcements Section */}
          <Card className="glass-dark border-white/10 mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Megaphone className="w-5 h-5 mr-2 text-blue-400" />
                Send Announcement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Form Section */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-gray-300">Announcement Title</Label>
                    <Input
                      value={announcementTitle}
                      onChange={(e) => setAnnouncementTitle(e.target.value)}
                      placeholder="e.g., New Feature Released!"
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                      maxLength={50}
                    />
                    <p className="text-xs text-gray-500 mt-1">{announcementTitle.length}/50 characters</p>
                  </div>

                  <div>
                    <Label className="text-gray-300">Message</Label>
                    <Textarea
                      value={announcementMessage}
                      onChange={(e) => setAnnouncementMessage(e.target.value)}
                      placeholder="Write your announcement message here..."
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 min-h-[120px]"
                      maxLength={200}
                    />
                    <p className="text-xs text-gray-500 mt-1">{announcementMessage.length}/200 characters</p>
                  </div>

                  <div>
                    <Label className="text-gray-300 mb-3 block">Target Audience</Label>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="role-all"
                          checked={selectedRoles.includes('all')}
                          onChange={() => handleRoleToggle('all')}
                          className="w-4 h-4 rounded"
                        />
                        <label htmlFor="role-all" className="text-white cursor-pointer">
                          All Users <span className="text-gray-400 text-sm">({userCounts.all})</span>
                        </label>
                      </div>

                      <div className="ml-6 space-y-2">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="role-free"
                            checked={selectedRoles.includes('free') || selectedRoles.includes('all')}
                            onChange={() => handleRoleToggle('free')}
                            disabled={selectedRoles.includes('all')}
                            className="w-4 h-4 rounded"
                          />
                          <label htmlFor="role-free" className={`cursor-pointer ${selectedRoles.includes('all') ? 'text-gray-500' : 'text-white'}`}>
                            Free Users <span className="text-gray-400 text-sm">({userCounts.free})</span>
                          </label>
                        </div>

                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="role-premium"
                            checked={selectedRoles.includes('premium') || selectedRoles.includes('all')}
                            onChange={() => handleRoleToggle('premium')}
                            disabled={selectedRoles.includes('all')}
                            className="w-4 h-4 rounded"
                          />
                          <label htmlFor="role-premium" className={`cursor-pointer ${selectedRoles.includes('all') ? 'text-gray-500' : 'text-white'}`}>
                            Premium Users <span className="text-gray-400 text-sm">({userCounts.premium})</span>
                          </label>
                        </div>

                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="role-tipster"
                            checked={selectedRoles.includes('tipster') || selectedRoles.includes('all')}
                            onChange={() => handleRoleToggle('tipster')}
                            disabled={selectedRoles.includes('all')}
                            className="w-4 h-4 rounded"
                          />
                          <label htmlFor="role-tipster" className={`cursor-pointer ${selectedRoles.includes('all') ? 'text-gray-500' : 'text-white'}`}>
                            Tipster Users <span className="text-gray-400 text-sm">({userCounts.tipster})</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={handleSendAnnouncement}
                    disabled={sendingAnnouncement || !announcementTitle.trim() || !announcementMessage.trim() || selectedRoles.length === 0}
                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  >
                    {sendingAnnouncement ? (
                      'Sending...'
                    ) : (
                      <>
                        <Megaphone className="w-4 h-4 mr-2" />
                        Send to {getTargetUserCount()} User(s)
                      </>
                    )}
                  </Button>
                </div>

                {/* Preview Section */}
                <div>
                  <Label className="text-gray-300 mb-3 block">Notification Preview</Label>
                  <div className="bg-slate-800/50 border border-white/10 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-white font-medium text-sm">
                            {announcementTitle ? `📢 ${announcementTitle}` : '📢 Announcement Title'}
                          </h4>
                          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                        </div>
                        <p className="text-gray-300 text-sm mb-2">
                          {announcementMessage || 'Your announcement message will appear here...'}
                        </p>
                        <p className="text-gray-500 text-xs">Just now</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <h5 className="text-blue-400 font-medium text-sm mb-2">📊 Announcement Stats</h5>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Total Recipients:</span>
                        <span className="text-white font-medium">{getTargetUserCount()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Target Groups:</span>
                        <span className="text-white font-medium">
                          {selectedRoles.length === 0 ? 'None' : selectedRoles.includes('all') ? 'All Users' : selectedRoles.map(r => r.charAt(0).toUpperCase() + r.slice(1)).join(', ')}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Delivery:</span>
                        <span className="text-white font-medium">In-App Notification</span>
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-gray-500 mt-4">
                    💡 <strong>Tip:</strong> Announcements will appear in the notification bell for selected users. Keep messages clear and concise.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Announcement History */}
          <Card className="glass-dark border-white/10 mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center">
                  <Megaphone className="w-5 h-5 mr-2 text-purple-400" />
                  Announcement History
                </CardTitle>
                {announcementPagination.total_items > 0 && (
                  <span className="text-sm text-gray-400">
                    {announcementPagination.total_items} total
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {announcementHistory.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  No announcements sent yet
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    {announcementHistory.map((announcement, index) => (
                    <div key={announcement.id || index} className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h4 className="text-white font-semibold mb-1">
                            📢 {announcement.title}
                          </h4>
                          <p className="text-gray-300 text-sm mb-2">
                            {announcement.message}
                          </p>
                        </div>
                        <Button
                          onClick={() => handleDeleteAnnouncement(announcement.id)}
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          title="Delete announcement"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm">
                        <div className="flex items-center gap-1">
                          <span className="text-gray-400">Sent:</span>
                          <span className="text-white">
                            {new Date(announcement.sent_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-1">
                          <span className="text-gray-400">Recipients:</span>
                          <span className="text-green-400 font-semibold">
                            {announcement.users_notified} users
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-1">
                          <span className="text-gray-400">Target:</span>
                          <span className="text-blue-400">
                            {announcement.target_roles.includes('all') 
                              ? 'All Users' 
                              : announcement.target_roles.map(r => r.charAt(0).toUpperCase() + r.slice(1)).join(', ')
                            }
                          </span>
                        </div>
                        
                        {announcement.emails_sent > 0 && (
                          <div className="flex items-center gap-1">
                            <span className="text-gray-400">Emails:</span>
                            <span className="text-purple-400">
                              {announcement.emails_sent}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  </div>
                  
                  {/* Pagination */}
                  {announcementPagination.total_pages > 1 && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                      <Button
                        onClick={() => fetchAnnouncementHistory(announcementPage - 1)}
                        disabled={announcementPage === 1}
                        size="sm"
                        variant="outline"
                        className="border-white/10 text-white hover:bg-white/10 disabled:opacity-50"
                      >
                        Previous
                      </Button>
                      <span className="text-sm text-gray-400">
                        Page {announcementPage} of {announcementPagination.total_pages}
                      </span>
                      <Button
                        onClick={() => fetchAnnouncementHistory(announcementPage + 1)}
                        disabled={announcementPage === announcementPagination.total_pages}
                        size="sm"
                        variant="outline"
                        className="border-white/10 text-white hover:bg-white/10 disabled:opacity-50"
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Subscription Plans Management */}
          <Card className="glass-dark border-white/10 mb-6">
            <CardHeader>
              <CardTitle className="text-white">Subscription Plans</CardTitle>
            </CardHeader>
            <CardContent>
              {plans && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {Object.entries(plans).map(([planId, planData]) => (
                    <div key={planId} className="glass p-4 rounded-lg">
                      <h3 className="text-lg font-bold text-white mb-3 capitalize">{planData.name}</h3>
                      
                      {/* Monthly Price */}
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-gray-400">Monthly Price:</span>
                        {editingPlan === planId ? (
                          <input
                            type="number"
                            step="0.01"
                            value={newPrice}
                            onChange={(e) => setNewPrice(e.target.value)}
                            className="w-24 px-2 py-1 bg-white/10 border border-white/20 rounded text-white"
                            placeholder={planData.price}
                          />
                        ) : (
                          <span className="text-white font-bold">
                            <Price amount={planData.price} />
                          </span>
                        )}
                      </div>

                      {/* Yearly Price (only for premium and tipster) */}
                      {(planId === 'premium' || planId === 'tipster') && (
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-gray-400">Yearly Price:</span>
                          {editingPlan === planId ? (
                            <input
                              type="number"
                              step="0.01"
                              value={newYearlyPrice}
                              onChange={(e) => setNewYearlyPrice(e.target.value)}
                              className="w-24 px-2 py-1 bg-white/10 border border-white/20 rounded text-white"
                              placeholder={planData.yearly_price || '0'}
                            />
                          ) : (
                            <span className="text-white font-bold">
                              <Price amount={planData.yearly_price || 0} />
                            </span>
                          )}
                        </div>
                      )}

                      {/* Discount Price (only for premium and tipster) - for backward compatibility */}
                      {(planId === 'premium' || planId === 'tipster') && (
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-gray-400">Discount Price:</span>
                          {editingPlan === planId ? (
                            <input
                              type="number"
                              step="0.01"
                              value={newDiscountPrice}
                              onChange={(e) => setNewDiscountPrice(e.target.value)}
                              className="w-24 px-2 py-1 bg-white/10 border border-white/20 rounded text-white"
                              placeholder={planData.discount_price || '0'}
                            />
                          ) : (
                            <span className="text-green-400 font-bold">
                              {planData.discount_price ? <Price amount={planData.discount_price} /> : 'None'}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Currency */}
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-gray-400">Currency:</span>
                        {editingPlan === planId ? (
                          <select
                            value={newCurrency}
                            onChange={(e) => setNewCurrency(e.target.value)}
                            className="w-24 px-2 py-1 bg-slate-800 border border-white/20 rounded text-white"
                            style={{ color: 'white', backgroundColor: '#1e293b' }}
                          >
                            <option value="" style={{ color: 'white', backgroundColor: '#1e293b' }}>Keep {(planData.currency || 'USD').toUpperCase()}</option>
                            <option value="usd" style={{ color: 'white', backgroundColor: '#1e293b' }}>USD</option>
                            <option value="eur" style={{ color: 'white', backgroundColor: '#1e293b' }}>EUR</option>
                            <option value="gbp" style={{ color: 'white', backgroundColor: '#1e293b' }}>GBP</option>
                            <option value="cad" style={{ color: 'white', backgroundColor: '#1e293b' }}>CAD</option>
                            <option value="aud" style={{ color: 'white', backgroundColor: '#1e293b' }}>AUD</option>
                          </select>
                        ) : (
                          <span className="text-white font-bold">
                            {(planData.currency || 'USD').toUpperCase()}
                          </span>
                        )}
                      </div>

                      {/* Show discount percentage if applicable */}
                      {planData.discount_percentage > 0 && editingPlan !== planId && (
                        <div className="mb-3 p-2 bg-green-500/20 border border-green-500/30 rounded">
                          <p className="text-green-400 text-sm font-medium text-center">
                            🎉 {planData.discount_percentage}% OFF
                          </p>
                        </div>
                      )}

                      {/* Edit/Save Buttons */}
                      {editingPlan === planId ? (
                        <div className="flex gap-2 mt-3">
                          <Button
                            size="sm"
                            onClick={() => handleUpdatePrice(planId)}
                            className="flex-1 bg-green-600 hover:bg-green-700"
                          >
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingPlan(null);
                              setNewPrice('');
                              setNewYearlyPrice('');
                              setNewDiscountPrice('');
                            }}
                            className="flex-1 text-gray-400 hover:bg-white/10"
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleEditPlan(planId, planData)}
                            className="w-full bg-blue-600 hover:bg-blue-700 mb-2"
                          >
                            Edit Prices
                          </Button>
                          
                          {/* Enable/Disable Toggle (only for premium and tipster) */}
                          {(planId === 'premium' || planId === 'tipster') && (
                            <div className="mt-3 pt-3 border-t border-white/10">
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-400">Plan Status:</span>
                                <label className="relative inline-flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={planData.enabled !== false}
                                    onChange={() => handleTogglePlan(planId, planData.enabled !== false)}
                                    className="sr-only peer"
                                  />
                                  <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                                </label>
                              </div>
                              <p className="text-xs mt-1 text-center">
                                <span className={planData.enabled !== false ? "text-green-400" : "text-gray-500"}>
                                  {planData.enabled !== false ? "✓ Enabled" : "✗ Disabled"}
                                </span>
                              </p>
                            </div>
                          )}
                        </>
                      )}

                      <p className="text-xs text-gray-500 mt-3">
                        {planData.bets_limit ? `Limit: ${planData.bets_limit} bets` : 'Unlimited bets'}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notification Statistics Section */}
          <Card className="glass-dark border-white/10 mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center justify-between">
                <span className="flex items-center">
                  <Mail className="w-5 h-5 mr-2" />
                  Email Notification Statistics
                </span>
                <Button
                  onClick={fetchNotificationStatistics}
                  disabled={loadingNotificationStats}
                  size="sm"
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  {loadingNotificationStats ? 'Refreshing...' : 'Refresh'}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingNotificationStats && !notificationStats ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto"></div>
                  <p className="text-gray-400 mt-4">Loading statistics...</p>
                </div>
              ) : notificationStats ? (
                <div className="space-y-6">
                  {/* Notification Subscription Counts */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4">📬 Notification Subscriptions</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 border border-blue-500/30 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-blue-400 font-medium">Betting Strategies</span>
                          <TrendingUp className="w-5 h-5 text-blue-400" />
                        </div>
                        <div className="text-3xl font-bold text-white">
                          {notificationStats.notification_counts.strategies}
                        </div>
                        <p className="text-sm text-gray-400 mt-1">users subscribed</p>
                      </div>
                      
                      <div className="bg-gradient-to-br from-green-500/20 to-green-600/20 border border-green-500/30 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-green-400 font-medium">Tip Services</span>
                          <Target className="w-5 h-5 text-green-400" />
                        </div>
                        <div className="text-3xl font-bold text-white">
                          {notificationStats.notification_counts.services}
                        </div>
                        <p className="text-sm text-gray-400 mt-1">users subscribed</p>
                      </div>
                      
                      <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 border border-purple-500/30 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-purple-400 font-medium">🤖 AI Predictions</span>
                          <Megaphone className="w-5 h-5 text-purple-400" />
                        </div>
                        <div className="text-3xl font-bold text-white">
                          {notificationStats.notification_counts.ai_predictions}
                        </div>
                        <p className="text-sm text-gray-400 mt-1">users subscribed</p>
                      </div>
                      
                      <div className="bg-gradient-to-br from-orange-500/20 to-orange-600/20 border border-orange-500/30 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-orange-400 font-medium">📝 Blog Articles</span>
                          <FileText className="w-5 h-5 text-orange-400" />
                        </div>
                        <div className="text-3xl font-bold text-white">
                          {notificationStats.notification_counts.blogs || 0}
                        </div>
                        <p className="text-sm text-gray-400 mt-1">users subscribed</p>
                      </div>
                    </div>
                  </div>

                  {/* Overall Email Engagement */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4">📊 Overall Email Engagement</h3>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      <div className="bg-white/5 rounded-lg p-4">
                        <div className="text-sm text-gray-400 mb-1">Total Sent</div>
                        <div className="text-2xl font-bold text-white">
                          {notificationStats.email_engagement.total_sent}
                        </div>
                      </div>
                      
                      <div className="bg-white/5 rounded-lg p-4">
                        <div className="text-sm text-gray-400 mb-1">Opened</div>
                        <div className="text-2xl font-bold text-green-400">
                          {notificationStats.email_engagement.total_opened}
                        </div>
                      </div>
                      
                      <div className="bg-white/5 rounded-lg p-4">
                        <div className="text-sm text-gray-400 mb-1">Clicked</div>
                        <div className="text-2xl font-bold text-blue-400">
                          {notificationStats.email_engagement.total_clicked}
                        </div>
                      </div>
                      
                      <div className="bg-white/5 rounded-lg p-4">
                        <div className="text-sm text-gray-400 mb-1">Open Rate</div>
                        <div className="text-2xl font-bold text-white">
                          {notificationStats.email_engagement.open_rate}%
                        </div>
                      </div>
                      
                      <div className="bg-white/5 rounded-lg p-4">
                        <div className="text-sm text-gray-400 mb-1">Click Rate</div>
                        <div className="text-2xl font-bold text-white">
                          {notificationStats.email_engagement.click_rate}%
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* AI Predictions Specific Stats */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4">🤖 AI Predictions Email Performance</h3>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                        <div className="text-sm text-purple-400 mb-1">Sent</div>
                        <div className="text-2xl font-bold text-white">
                          {notificationStats.ai_predictions_stats.sent}
                        </div>
                      </div>
                      
                      <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                        <div className="text-sm text-purple-400 mb-1">Opened</div>
                        <div className="text-2xl font-bold text-green-400">
                          {notificationStats.ai_predictions_stats.opened}
                        </div>
                      </div>
                      
                      <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                        <div className="text-sm text-purple-400 mb-1">Clicked</div>
                        <div className="text-2xl font-bold text-blue-400">
                          {notificationStats.ai_predictions_stats.clicked}
                        </div>
                      </div>
                      
                      <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                        <div className="text-sm text-purple-400 mb-1">Open Rate</div>
                        <div className="text-2xl font-bold text-white">
                          {notificationStats.ai_predictions_stats.open_rate}%
                        </div>
                      </div>
                      
                      <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                        <div className="text-sm text-purple-400 mb-1">Click Rate</div>
                        <div className="text-2xl font-bold text-white">
                          {notificationStats.ai_predictions_stats.click_rate}%
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Strategies Email Stats */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4">🎯 Strategies Email Performance</h3>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-4">
                        <div className="text-sm text-indigo-400 mb-1">Sent</div>
                        <div className="text-2xl font-bold text-white">
                          {notificationStats.strategies_stats?.sent || 0}
                        </div>
                      </div>
                      
                      <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-4">
                        <div className="text-sm text-indigo-400 mb-1">Opened</div>
                        <div className="text-2xl font-bold text-green-400">
                          {notificationStats.strategies_stats?.opened || 0}
                        </div>
                      </div>
                      
                      <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-4">
                        <div className="text-sm text-indigo-400 mb-1">Clicked</div>
                        <div className="text-2xl font-bold text-blue-400">
                          {notificationStats.strategies_stats?.clicked || 0}
                        </div>
                      </div>
                      
                      <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-4">
                        <div className="text-sm text-indigo-400 mb-1">Open Rate</div>
                        <div className="text-2xl font-bold text-white">
                          {notificationStats.strategies_stats?.open_rate || 0}%
                        </div>
                      </div>
                      
                      <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-4">
                        <div className="text-sm text-indigo-400 mb-1">Click Rate</div>
                        <div className="text-2xl font-bold text-white">
                          {notificationStats.strategies_stats?.click_rate || 0}%
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Services Email Stats */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4">💡 Tip Services Email Performance</h3>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                        <div className="text-sm text-blue-400 mb-1">Sent</div>
                        <div className="text-2xl font-bold text-white">
                          {notificationStats.services_stats?.sent || 0}
                        </div>
                      </div>
                      
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                        <div className="text-sm text-blue-400 mb-1">Opened</div>
                        <div className="text-2xl font-bold text-green-400">
                          {notificationStats.services_stats?.opened || 0}
                        </div>
                      </div>
                      
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                        <div className="text-sm text-blue-400 mb-1">Clicked</div>
                        <div className="text-2xl font-bold text-blue-400">
                          {notificationStats.services_stats?.clicked || 0}
                        </div>
                      </div>
                      
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                        <div className="text-sm text-blue-400 mb-1">Open Rate</div>
                        <div className="text-2xl font-bold text-white">
                          {notificationStats.services_stats?.open_rate || 0}%
                        </div>
                      </div>
                      
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                        <div className="text-sm text-blue-400 mb-1">Click Rate</div>
                        <div className="text-2xl font-bold text-white">
                          {notificationStats.services_stats?.click_rate || 0}%
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Blogs Email Stats */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4">📝 Blog Email Performance</h3>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                        <div className="text-sm text-cyan-400 mb-1">Sent</div>
                        <div className="text-2xl font-bold text-white">
                          {notificationStats.blogs_stats?.sent || 0}
                        </div>
                      </div>
                      
                      <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                        <div className="text-sm text-cyan-400 mb-1">Opened</div>
                        <div className="text-2xl font-bold text-green-400">
                          {notificationStats.blogs_stats?.opened || 0}
                        </div>
                      </div>
                      
                      <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                        <div className="text-sm text-cyan-400 mb-1">Clicked</div>
                        <div className="text-2xl font-bold text-blue-400">
                          {notificationStats.blogs_stats?.clicked || 0}
                        </div>
                      </div>
                      
                      <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                        <div className="text-sm text-cyan-400 mb-1">Open Rate</div>
                        <div className="text-2xl font-bold text-white">
                          {notificationStats.blogs_stats?.open_rate || 0}%
                        </div>
                      </div>
                      
                      <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                        <div className="text-sm text-cyan-400 mb-1">Click Rate</div>
                        <div className="text-2xl font-bold text-white">
                          {notificationStats.blogs_stats?.click_rate || 0}%
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Users with Notifications Enabled */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4">👥 Users with Notifications Enabled ({notificationStats.users.length})</h3>
                    {notificationStats.users.length > 0 ? (
                      <div className="bg-white/5 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-white/10">
                              <tr>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">User</th>
                                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-300">Strategies</th>
                                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-300">Services</th>
                                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-300">AI Predictions</th>
                                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-300">Blog</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-white/10">
                              {notificationStats.users.map((u, index) => (
                                <tr key={index} className="hover:bg-white/5">
                                  <td className="px-4 py-3">
                                    <div className="text-white font-medium">{u.name}</div>
                                    <div className="text-sm text-gray-400">{u.email}</div>
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    {u.notify_new_strategies ? (
                                      <CheckCircle className="w-5 h-5 text-green-400 mx-auto" />
                                    ) : (
                                      <XCircle className="w-5 h-5 text-gray-600 mx-auto" />
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    {u.notify_new_services ? (
                                      <CheckCircle className="w-5 h-5 text-green-400 mx-auto" />
                                    ) : (
                                      <XCircle className="w-5 h-5 text-gray-600 mx-auto" />
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    {u.notify_ai_predictions ? (
                                      <CheckCircle className="w-5 h-5 text-purple-400 mx-auto" />
                                    ) : (
                                      <XCircle className="w-5 h-5 text-gray-600 mx-auto" />
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    {u.notify_new_blogs ? (
                                      <CheckCircle className="w-5 h-5 text-orange-400 mx-auto" />
                                    ) : (
                                      <XCircle className="w-5 h-5 text-gray-600 mx-auto" />
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-white/5 rounded-lg">
                        <p className="text-gray-400">No users have enabled any email notifications yet.</p>
                      </div>
                    )}
                  </div>

                  {/* Tips for improvement */}
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <h4 className="text-white font-semibold mb-2">💡 Insights & Tips</h4>
                    <ul className="text-sm text-gray-300 space-y-1">
                      <li>• High open rates (&gt;20%) indicate good email timing and relevance</li>
                      <li>• Click rates (&gt;5%) show users are engaged with your content</li>
                      <li>• Consider A/B testing different email send times for better engagement</li>
                      <li>• Users who enable AI predictions are likely your most engaged audience</li>
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-400">No notification statistics available.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* User Management Quick Access Card */}
          <Card className="glass-dark border-white/10">
            <CardContent className="pt-6">
              <div className="flex flex-col items-center justify-center py-8">
                <Users className="w-16 h-16 text-purple-400 mb-4" />
                <h3 className="text-2xl font-bold text-white mb-2">User Management</h3>
                <p className="text-gray-400 text-center mb-6 max-w-md">
                  Access the comprehensive User Management (CRM) system to manage users, subscriptions, billing, and more.
                </p>
                <Button
                  onClick={() => navigate('/admin/crm')}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Open User Management
                </Button>
                <div className="grid grid-cols-3 gap-8 mt-8">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-white">{users.length}</div>
                    <div className="text-sm text-gray-400">Total Users</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-400">
                      {users.filter(u => u.subscription_plan === 'premium').length}
                    </div>
                    <div className="text-sm text-gray-400">Premium Users</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-purple-400">
                      {users.filter(u => u.subscription_plan === 'tipster').length}
                    </div>
                    <div className="text-sm text-gray-400">Tipster Users</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Confirmation Dialogs */}
      <ConfirmDialog
        open={deleteUserDialog.open}
        onOpenChange={(open) => setDeleteUserDialog({ open, userId: null })}
        title="Delete User"
        description="Are you sure you want to delete this user? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
        onConfirm={confirmDeleteUser}
      />

      <ConfirmDialog
        open={announcementDialog.open}
        onOpenChange={(open) => setAnnouncementDialog({ open: false, count: 0 })}
        title="Send Announcement"
        description={`Send announcement to ${announcementDialog.count} user(s)?`}
        confirmText="Send"
        cancelText="Cancel"
        variant="default"
        onConfirm={confirmSendAnnouncement}
      />

      {/* Email Modal */}
      {emailModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="glass-dark border-white/10 max-w-2xl w-full">
            <CardHeader>
              <CardTitle className="text-white flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Send Email to {emailModal.user?.email}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEmailModal({ open: false, user: null })}
                  className="text-white hover:bg-white/10"
                >
                  ✕
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-white mb-2">Subject</Label>
                <Input
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Email subject"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>

              <div>
                <Label className="text-white mb-2">Message</Label>
                <Textarea
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  placeholder="Email message (HTML supported)"
                  className="bg-white/5 border-white/10 text-white min-h-[200px]"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleSendEmail}
                  disabled={sendingEmail}
                  className="bg-blue-600 hover:bg-blue-700 flex-1"
                >
                  {sendingEmail ? 'Sending...' : 'Send Email'}
                </Button>
                <Button
                  onClick={() => setEmailModal({ open: false, user: null })}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 flex-1"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </Layout>
  );
};

export default AdminDashboard;
